import type { ViewColumn, ViewRow } from "@/lib/boardView";
import type { FilterCond } from "@/lib/types";

// Column types we offer a filter control for.
export const FILTERABLE_TYPES = ["status", "tags", "text", "long_text", "checkbox", "date"];

// Sentinel option id (F10): when selected in a status/tags "in" filter it matches
// rows with NO (valid) value — "Ohne Status" / "Leer". Real option ids are slugs or
// uuids, never this token, so it can't collide with a genuine option.
export const EMPTY_FILTER_VALUE = "__empty__";

function cellValue(row: ViewRow, columnId: string): unknown {
  return row.raw?.cells?.find((c) => c.column_def_id === columnId)?.value;
}

// A condition only constrains rows once it actually holds a value — an empty
// status set / blank text / unset date is inactive (passes everything).
export function isActiveFilter(c: FilterCond): boolean {
  if (c.op === "in") return Array.isArray(c.value) && c.value.length > 0;
  if (c.op === "contains") return typeof c.value === "string" && c.value.trim() !== "";
  if (c.op === "is") return typeof c.value === "boolean";
  if (c.op === "before" || c.op === "after") return typeof c.value === "string" && c.value !== "";
  return false;
}

export function activeFilterCount(filters: FilterCond[] | undefined): number {
  return (filters ?? []).filter(isActiveFilter).length;
}

function condPasses(col: ViewColumn, cond: FilterCond, v: unknown): boolean {
  switch (cond.op) {
    case "in": {
      const allowed = Array.isArray(cond.value) ? cond.value : [];
      const wantsEmpty = allowed.includes(EMPTY_FILTER_VALUE);
      if (col.type === "tags") {
        const tags = Array.isArray(v) ? v : [];
        if (wantsEmpty && tags.length === 0) return true;
        return tags.some((tag) => allowed.includes(String(tag)));
      }
      // status (single value): "empty" = no value or a value that isn't a current
      // option (orphaned), matching how grouping buckets it into "Ohne …".
      const validIds = new Set((col.options ?? []).map((o) => o.id));
      const isEmpty = typeof v !== "string" || v === "" || !validIds.has(v);
      if (wantsEmpty && isEmpty) return true;
      return typeof v === "string" && allowed.includes(v);
    }
    case "contains": {
      const needle = typeof cond.value === "string" ? cond.value.trim().toLowerCase() : "";
      return typeof v === "string" && v.toLowerCase().includes(needle);
    }
    case "is":
      // checkbox: value true → only checked rows; false → only unchecked.
      return (v === true) === (cond.value === true);
    case "before":
    case "after": {
      if (typeof v !== "string" || !v || typeof cond.value !== "string") return false;
      const a = new Date(v).getTime();
      const b = new Date(cond.value).getTime();
      if (Number.isNaN(a) || Number.isNaN(b)) return false;
      return cond.op === "before" ? a < b : a > b;
    }
    default:
      return true;
  }
}

export function rowPassesFilters(
  row: ViewRow,
  filters: FilterCond[],
  columns: ViewColumn[],
): boolean {
  for (const cond of filters) {
    if (!isActiveFilter(cond)) continue;
    const col = columns.find((c) => c.id === cond.column_id);
    if (!col) continue;
    if (!condPasses(col, cond, cellValue(row, cond.column_id))) return false;
  }
  return true;
}

// Filter rows, keeping every child of a passing top-level row (sub-items aren't
// filtered independently — they belong to their parent's row).
export function filterRows(
  rows: ViewRow[],
  filters: FilterCond[] | undefined,
  columns: ViewColumn[],
): ViewRow[] {
  const active = (filters ?? []).filter(isActiveFilter);
  if (active.length === 0) return rows;
  const topPass = new Set(
    rows.filter((r) => !r.parentRowId && rowPassesFilters(r, active, columns)).map((r) => r.id),
  );
  return rows.filter((r) => (r.parentRowId ? topPass.has(r.parentRowId) : topPass.has(r.id)));
}
